// References of the registry: `kind/[project:]name[@N]`, the kind written as its slug.
import { KINDS, type KindSlug } from "@cosmosai/specs/kinds";

export interface SpecRef {
  slug: KindSlug;
  /** The project of project-level kinds; null for workspace-level kinds and scopes. */
  project: string | null;
  name: string;
  version: number | null;
}

const NAME = "[a-z0-9](?:[-a-z0-9]{0,61}[a-z0-9])?";
const PATTERN = new RegExp(
  `^(?<slug>[a-z-]+)/(?:(?<project>${NAME}):)?(?<name>${NAME})(?:@(?<version>[1-9][0-9]{0,15}))?$`,
);

export class RefError extends Error {
  override name = "RefError";
}

export const isKindSlug = (value: string): value is KindSlug => Object.hasOwn(KINDS, value);

/** Parses a reference such as `agent/finance:kbis@3`; throws a RefError when it is not one. */
export function parseRef(text: string): SpecRef {
  const match = PATTERN.exec(text);
  const groups = match?.groups;
  if (!groups?.slug || !groups.name) {
    throw new RefError(`expected kind/name@N or kind/project:name@N, got "${text}"`);
  }
  if (!isKindSlug(groups.slug)) throw new RefError(`unknown kind "${groups.slug}" in "${text}"`);
  return {
    slug: groups.slug,
    project: groups.project ?? null,
    name: groups.name,
    version: groups.version ? Number(groups.version) : null,
  };
}

/** The reference, or null when the text is not one. */
export function tryParseRef(text: string): SpecRef | null {
  try {
    return parseRef(text);
  } catch {
    return null;
  }
}

/** Writes a reference back; `version` overrides the reference's own (null drops it). */
export function formatRef(ref: SpecRef, version: number | null = ref.version): string {
  const project = ref.project ? `${ref.project}:` : "";
  return `${ref.slug}/${project}${ref.name}${version === null ? "" : `@${version}`}`;
}
